import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { getDashboardStats, getAnnouncements } from '@/services/api';
import { FileText, CreditCard, Calendar, Users, Bell, TrendingUp, CalendarDays, Home } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const ResidentHome = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsData, announcementsData] = await Promise.all([
        getDashboardStats(),
        getAnnouncements()
      ]);
      setStats(statsData);
      setAnnouncements(announcementsData.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Pending Bills',
      value: stats?.pending_bills || 0,
      icon: CreditCard,
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      action: () => navigate('/resident/bills')
    },
    {
      title: 'Open Complaints',
      value: stats?.open_complaints || 0,
      icon: FileText,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      action: () => navigate('/resident/complaints')
    },
    {
      title: 'Upcoming Bookings',
      value: stats?.upcoming_bookings || 0,
      icon: Calendar,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      action: () => navigate('/resident/amenities')
    },
    {
      title: 'Registered Events',
      value: stats?.registered_events || 0,
      icon: CalendarDays,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      action: () => navigate('/resident/events')
    },
    {
      title: 'Pending Visitors',
      value: stats?.pending_visitors || 0,
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      action: () => navigate('/resident/visitors')
    },
    {
      title: 'Active Tenants',
      value: stats?.active_tenants || 0,
      icon: Home,
      color: 'text-teal-600',
      bgColor: 'bg-teal-50',
      action: () => navigate('/resident/tenants')
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-secondary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="resident-dashboard">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Dashboard</h1>
        <p className="text-slate-600">Welcome to your FortiHome dashboard</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card
              key={index}
              className="stat-card cursor-pointer border-slate-200 shadow-sm hover:shadow-md"
              onClick={stat.action}
              data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                  </div>
                  <div className={`h-12 w-12 ${stat.bgColor} rounded-xl flex items-center justify-center`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Frequently used features</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button
              className="h-20 bg-secondary hover:bg-secondary/90"
              onClick={() => navigate('/resident/bills')}
              data-testid="quick-action-pay-bills"
            >
              <CreditCard className="h-5 w-5 mr-2" />
              Pay Bills
            </Button>
            <Button
              className="h-20 bg-accent hover:bg-accent/90"
              onClick={() => navigate('/resident/amenities')}
              data-testid="quick-action-book-amenity"
            >
              <Calendar className="h-5 w-5 mr-2" />
              Book Amenity
            </Button>
            <Button
              className="h-20 bg-primary hover:bg-primary/90"
              onClick={() => navigate('/resident/chat')}
              data-testid="quick-action-ai-assistant"
            >
              <Bell className="h-5 w-5 mr-2" />
              AI Assistant
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Announcements */}
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="h-5 w-5" />
            Recent Announcements
          </CardTitle>
        </CardHeader>
        <CardContent>
          {announcements.length === 0 ? (
            <p className="text-slate-500 text-center py-8">No announcements yet</p>
          ) : (
            <div className="space-y-4">
              {announcements.map((announcement) => (
                <div
                  key={announcement.id}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                  data-testid="announcement-item"
                >
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold text-slate-900">{announcement.title}</h3>
                    <span className="text-xs text-slate-500">
                      {new Date(announcement.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600">{announcement.content}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResidentHome;